import React, { useEffect, useId, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Send, X } from 'lucide-react';
import MascotMark from './MascotMark';
import { MASCOT_NAME, answerQuestion, introForPage, resolveView, suggestionsFor } from './helpTopics';

const HELLO_KEY = 'fashdeck.mira.helloDismissed';

let messageSeq = 0;
const nextMessageId = () => {
  messageSeq += 1;
  return messageSeq;
};

const MascotHelp = ({
  signedIn = false,
  liteModeEnabled = false,
  pageId = '',
  pageLabel = '',
  onNavigate,
  onRequestSupport,
  hidden = false,
  clearFooter = false,
  raised = false,
}) => {
  const reduceMotion = useReducedMotion();
  const panelId = useId();
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [showHello, setShowHello] = useState(false);
  const [messages, setMessages] = useState(() => [
    { id: nextMessageId(), from: 'mira', intro: true, text: introForPage(pageId, pageLabel) },
  ]);

  useEffect(() => {
    setMessages((prev) => {
      if (!prev.length || !prev[0].intro) return prev;
      const text = introForPage(pageId, pageLabel);
      if (prev[0].text === text) return prev;
      return [{ ...prev[0], text }, ...prev.slice(1)];
    });
  }, [pageId, pageLabel]);

  useEffect(() => {
    let dismissed = false;
    try {
      dismissed = sessionStorage.getItem(HELLO_KEY) === '1';
    } catch (_) {
      dismissed = false;
    }
    if (dismissed) return undefined;
    const timer = window.setTimeout(() => setShowHello(true), 700);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    const focusTimer = window.setTimeout(() => inputRef.current?.focus(), 40);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.clearTimeout(focusTimer);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    window.addEventListener('pointerdown', onPointerDown);
    return () => window.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    list.scrollTop = list.scrollHeight;
  }, [messages, open]);

  const dismissHello = () => {
    setShowHello(false);
    try {
      sessionStorage.setItem(HELLO_KEY, '1');
    } catch (_) {
      /* storage can be blocked */
    }
  };

  const pushAnswer = (text) => {
    const result = answerQuestion(text, pageId);
    setMessages((prev) => [
      ...prev,
      { id: nextMessageId(), from: 'you', text },
      {
        id: nextMessageId(),
        from: 'mira',
        text: result?.text || "Ask me about fabrics, concepts, Studio, or credits.",
        actions: result?.actions || [],
      },
    ]);
  };

  const ask = (text) => {
    const trimmed = String(text || '').trim();
    if (!trimmed) return;
    dismissHello();
    setDraft('');
    pushAnswer(trimmed);
  };

  const handleAction = (action) => {
    if (action.kind === 'support') {
      setOpen(false);
      onRequestSupport?.();
      return;
    }
    if (!action.view) return;
    const view = !signedIn && action.view !== 'pricing'
      ? 'login'
      : resolveView(action.view, liteModeEnabled);
    onNavigate?.(view);
    setOpen(false);
  };

  if (hidden) return null;

  const position = raised
    ? 'bottom-32 md:bottom-28'
    : clearFooter
      ? 'bottom-[max(1rem,env(safe-area-inset-bottom))] md:bottom-24'
      : 'bottom-[max(1rem,env(safe-area-inset-bottom))]';

  return (
    <div ref={rootRef} className={`fixed right-4 z-[95] ${position} flex flex-col items-end`}>
      {open && (
          <motion.section
            id={panelId}
            role="dialog"
            aria-label={`${MASCOT_NAME}, Fashdeck guide`}
            initial={reduceMotion ? false : { y: 8 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.16 }}
            className="mb-3 flex w-[min(22.5rem,calc(100vw-2rem))] max-h-[min(32rem,calc(100dvh-8rem))] min-h-0 flex-col overflow-hidden rounded-3xl border border-[#e7e2da] bg-[#fffcf8] shadow-[0_18px_50px_rgba(20,20,19,0.18)]"
          >
            <header className="flex items-center gap-3 bg-[#141413] px-4 py-3 text-[#faf6f0]">
              <span className="relative h-11 w-11 shrink-0 overflow-hidden rounded-full bg-[#faf6f0]">
                <MascotMark className="absolute left-1/2 top-1 h-16 w-16 -translate-x-1/2" waving={false} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold tracking-tight">{MASCOT_NAME}</span>
                <span className="block text-xs text-[#faf6f0]/70">Your Fashdeck guide</span>
              </span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-full p-2 text-[#faf6f0]/80 hover:bg-white/10 hover:text-white"
                aria-label={`Close ${MASCOT_NAME}`}
              >
                <X size={16} />
              </button>
            </header>

            <div ref={listRef} className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-4" aria-live="polite">
              {messages.map((message) => (
                <div key={message.id} className={message.from === 'you' ? 'flex justify-end' : 'flex justify-start'}>
                  <div
                    className={
                      message.from === 'you'
                        ? 'max-w-[85%] rounded-2xl rounded-br-md bg-[#5424e4] px-3 py-2 text-sm leading-relaxed text-white'
                        : 'max-w-[90%] rounded-2xl rounded-bl-md bg-white px-3 py-2 text-sm leading-relaxed text-[#141413] shadow-sm ring-1 ring-[#ece7e0]'
                    }
                  >
                    <p>{message.text}</p>
                    {message.actions?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {message.actions.map((action) => (
                          <button
                            key={`${action.kind || action.view}-${action.label}`}
                            type="button"
                            onClick={() => handleAction(action)}
                            className="rounded-full bg-[#141413] px-3 py-1.5 text-xs font-medium text-white hover:bg-black"
                          >
                            {signedIn || action.kind === 'support' || action.view === 'pricing'
                              ? action.label
                              : action.publicLabel || action.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-[#efeae3] bg-white px-3 pb-3 pt-2">
              <div className="mb-2 flex flex-wrap gap-2">
                {suggestionsFor(pageId).map((suggestion) => (
                  <button
                    key={suggestion}
                    type="button"
                    onClick={() => ask(suggestion)}
                    className="shrink-0 rounded-full border border-[#e7e2da] bg-[#faf6f0] px-3 py-1 text-xs font-medium text-[#3d3a36] hover:border-[#d97757] hover:text-[#141413]"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
              <form
                className="flex items-center gap-2"
                onSubmit={(event) => {
                  event.preventDefault();
                  ask(draft);
                }}
              >
                <input
                  ref={inputRef}
                  value={draft}
                  onChange={(event) => setDraft(event.target.value)}
                  placeholder={`Ask ${MASCOT_NAME}…`}
                  aria-label={`Message ${MASCOT_NAME}`}
                  className="min-w-0 flex-1 rounded-full border border-[#e7e2da] bg-[#fffcf8] px-4 py-2.5 text-sm text-[#141413] outline-none ring-[#5424e4]/30 placeholder:text-[#8a847b] focus:ring-2"
                />
                <button
                  type="submit"
                  disabled={!draft.trim()}
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#d97757] text-white disabled:bg-[#e7e2da] disabled:text-[#8a847b]"
                  aria-label="Send"
                >
                  <Send size={16} />
                </button>
              </form>
            </div>
          </motion.section>
        )}

      <div className="relative">
          {!open && showHello && (
            <div className="absolute bottom-3 right-full z-10 mr-3 w-[11.5rem] rounded-2xl bg-white px-3 py-2 text-sm leading-snug text-[#141413] shadow-lg ring-1 ring-[#ece7e0]">
              <button
                type="button"
                onClick={dismissHello}
                className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-[#141413] text-white"
                aria-label="Dismiss greeting"
              >
                <X size={12} />
              </button>
              <p>Hi, I&apos;m {MASCOT_NAME}. Need a hand?</p>
              <span className="absolute -right-1.5 bottom-4 h-3 w-3 rotate-45 bg-white" />
            </div>
          )}

      <button
        type="button"
        onClick={() => {
          dismissHello();
          setOpen((value) => !value);
        }}
        className="relative h-[4.5rem] w-[4.5rem]"
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={open ? `Close ${MASCOT_NAME}` : `Ask ${MASCOT_NAME} for help`}
      >
        <span className="absolute inset-0 rounded-full bg-white shadow-[0_12px_30px_rgba(20,20,19,0.2)] ring-2 ring-[#141413]" />
        <MascotMark
          waving={!open && !reduceMotion}
          className="pointer-events-none absolute left-1/2 top-[-1.35rem] h-[6.1rem] w-[6.1rem] -translate-x-1/2"
        />
      </button>
      </div>
    </div>
  );
};

export default MascotHelp;
